import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getContact,
  listContactDeals,
  listContactDuplicates,
  listContactIds,
  listContactMatches,
  listContacts,
  listContactSources,
  listContactsPage,
  updateContact,
  deleteContact,
  bulkAssignOwner,
  mergeContact,
  type ContactFilterParams,
  type ContactPageParams,
  type CreateContactParams,
  type UpdateContactParams,
} from '@/api/contacts'
import { api } from '@/api/client'
import type { Contact } from '@/api/types'
import { listFieldDefinitions } from '@/api/fields'

export function useContacts() {
  return useQuery({ queryKey: ['contacts'], queryFn: () => listContacts() })
}

/**
 * Offset-paginated, server-sorted/filtered contacts for the table view.
 * keepPreviousData keeps the current page on screen while the next one loads,
 * so paging and sorting don't flash an empty table.
 */
export function useContactsPage(params: ContactPageParams) {
  return useQuery({
    queryKey: ['contacts', 'page', params],
    queryFn: () => listContactsPage(params),
    placeholderData: keepPreviousData,
  })
}

/** Distinct lead sources for the Source filter dropdown, optionally narrowed to the selected owner. */
export function useContactSources(owners?: string[]) {
  return useQuery({
    queryKey: ['contacts', 'sources', owners ?? null],
    queryFn: () => listContactSources(owners),
  })
}

/**
 * The ids of every contact matching the current filter — backs the table's "select all matching".
 * Lazy: only fetched once {@code enabled} (i.e. the user clicked "select all matching"), and keyed
 * by the filter so a filter change refetches the right set.
 */
export function useContactIds(filters: ContactFilterParams, enabled: boolean) {
  return useQuery({
    queryKey: ['contacts', 'ids', filters],
    queryFn: () => listContactIds(filters),
    enabled,
  })
}

/**
 * Server-side contact search (name/email substring, case-insensitive).
 * Disabled while q is empty — callers fall back to the first page (useContacts).
 */
export function useContactSearch(q: string) {
  const query = q.trim()
  return useQuery({
    queryKey: ['contacts', 'search', query],
    queryFn: () => listContacts(null, 50, query),
    enabled: query.length > 0,
    placeholderData: (prev) => prev, // keep the previous matches while the next ones load
  })
}

/**
 * Existing contacts sharing the e-mail or phone being typed into the Add contact form — fetched once
 * the e-mail looks complete or the phone has 6+ digits, so a duplicate is flagged before it's saved.
 */
export function useContactMatches(email: string, phone: string) {
  const e = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? email.trim() : ''
  const p = phone.replace(/\D/g, '').length >= 6 ? phone.trim() : ''
  return useQuery({
    queryKey: ['contacts', 'matches', e, p],
    queryFn: () => listContactMatches(e, p),
    enabled: !!(e || p),
  })
}

export function useContact(id?: string) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: () => getContact(id as string),
    enabled: !!id,
  })
}

export interface CreateContactResult {
  contact: Contact
  /** false when the backend merged into an existing contact (200) instead of creating one (201). */
  created: boolean
}

/** POST /api/contacts — the backend upserts by email: 201 created / 200 merged into an existing contact. */
export async function upsertContact(params: CreateContactParams): Promise<CreateContactResult> {
  const res = await api.post<Contact>('/api/contacts', params)
  return { contact: res.data, created: res.status === 201 }
}

export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: upsertContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  })
}

export function useUpdateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateContactParams }) =>
      updateContact(id, params),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contact', c.id] })
      // The contact's name/email is denormalised into a deal's Contacts panel and into
      // list rows, so those must refresh when it changes.
      qc.invalidateQueries({ queryKey: ['deal-contacts'] })
      qc.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

/** Merge a duplicate (source) contact into the target (admin-only). */
export function useMergeContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ targetId, sourceId }: { targetId: string; sourceId: string }) =>
      mergeContact(targetId, sourceId),
    onSuccess: (_data, { targetId, sourceId }) => {
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contact', targetId] })
      qc.invalidateQueries({ queryKey: ['contact-activities', targetId] })
      qc.invalidateQueries({ queryKey: ['contact-deals', targetId] })
      qc.invalidateQueries({ queryKey: ['deal-contacts'] })
      qc.invalidateQueries({ queryKey: ['lists'] })
      // The duplicate is GONE: refresh the duplicates banner (it still listed the merged-away
      // contact, and merging it a second time failed with "Contact not found"), and drop every
      // cached query keyed by the deleted id so nothing refetches it.
      qc.invalidateQueries({ queryKey: ['contact-duplicates'] })
      qc.removeQueries({ queryKey: ['contact', sourceId] })
      qc.removeQueries({ queryKey: ['contact-activities', sourceId] })
      qc.removeQueries({ queryKey: ['contact-deals', sourceId] })
    },
  })
}

/** Bulk-reassign selected contacts to one owner (admin-only). */
export function useBulkAssignOwner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contactIds, ownerUserId }: { contactIds: string[]; ownerUserId: string | null }) =>
      bulkAssignOwner(contactIds, ownerUserId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  })
}

export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: (_data, id) => {
      // Deleting cascades the contact's deal links, list memberships and activities, so refresh
      // everything that referenced it.
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['deal-contacts'] })
      qc.invalidateQueries({ queryKey: ['contact-deals'] })
      qc.invalidateQueries({ queryKey: ['lists'] })
      // Someone else's duplicates banner may still list this contact; and nothing should refetch it.
      qc.invalidateQueries({ queryKey: ['contact-duplicates'] })
      qc.removeQueries({ queryKey: ['contact', id] })
      qc.removeQueries({ queryKey: ['contact-activities', id] })
      qc.removeQueries({ queryKey: ['contact-deals', id] })
    },
  })
}

export function useContactDeals(contactId?: string) {
  return useQuery({
    queryKey: ['contact-deals', contactId],
    queryFn: () => listContactDeals(contactId as string),
    enabled: !!contactId,
  })
}

export function useContactDuplicates(contactId?: string) {
  return useQuery({
    queryKey: ['contact-duplicates', contactId],
    queryFn: () => listContactDuplicates(contactId as string),
    enabled: !!contactId,
  })
}

export function useFieldDefinitions(entityType: 'contact' | 'deal') {
  return useQuery({
    queryKey: ['fields', entityType],
    queryFn: () => listFieldDefinitions(entityType),
    staleTime: 5 * 60_000, // definitions change rarely — avoid refetch churn on every card open
  })
}
